import { Spinner, Typography } from '@tempmee/react';
import moment from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { hideConfirmCandidateEligibleJobsPopup, SHOW_MESSAGE } from '~/actions';
import { colors } from '@tempmee/tailwind';
import { Box, Button, Center, Row } from '~/commonComponents';
import Portal from '~/commonComponents/Portal';
import { useInfiniteEligibleJobs } from '~/domain/EligibleJobs';
import {
  IInviteProfessionalsToJobsPayload,
  jobKeys,
  useInviteProfessionalsToJobsMutation,
} from '~/domain/Jobs';
import { useAppSelector } from '~/hooks';
import ClosePopupIcon from '~/images/ClosePopupIcon.svg';
import defaultProfile from '~/images/HY_tooth.png';
import { routes } from '~/routes';
import { EligibleJobCard } from './EligibleJobCard';

export const ConfirmCandidateEligibleJobsPopup = ({
  navigateWithoutBlocking,
}: {
  navigateWithoutBlocking: (path: string) => void;
}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const inviteProfessionalsToJobsMutation = useInviteProfessionalsToJobsMutation();
  const [selectedJobIds, setSelectedJobIds] = useState<string[]>([]);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { show, candidate, professionalId, officeId, job } = useAppSelector(
    (state) => state.job.confirmCandidateEligibleJobsPopup,
  );
  const isInviting = inviteProfessionalsToJobsMutation.isPending;

  const { eligibleJobs, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteEligibleJobs({
      xOfficeId: officeId,
      professionalId: professionalId,
      limit: 10,
      enabled: show,
      profession: job?.profession,
    });

  const handleJobSelect = useCallback((jobId: string) => {
    setSelectedJobIds((prev) => {
      if (prev.includes(jobId)) {
        return prev.filter((id) => id !== jobId);
      }
      return [...prev, jobId];
    });
  }, []);

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const scrolledToBottom = scrollTop + clientHeight >= scrollHeight - 50;

    if (scrolledToBottom && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  const handlePopupClose = useCallback(
    (route: string) => {
      dispatch(hideConfirmCandidateEligibleJobsPopup());
      setSelectedJobIds([]);
      navigateWithoutBlocking(route);
    },
    [dispatch, navigateWithoutBlocking],
  );

  const onClose = () => {
    handlePopupClose('/dashboard/jobs/scheduled');
  };

  const handleInviteSuccess = useCallback(
    (_data: unknown, payload: IInviteProfessionalsToJobsPayload) => {
      payload.jobIds.forEach((jobId) => {
        queryClient.invalidateQueries({ queryKey: jobKeys.detail(jobId) });
      });
      handlePopupClose('/');
      dispatch({
        type: SHOW_MESSAGE,
        payload: {
          isSuccess: true,
          popupProps: {
            rightButtonText: 'Close',
          },
          title: 'Request(s) Successfully Sent!',
          message: (
            <>
              <b>{candidate?.full_name}</b> has been invited to the selected shift(s). Once they
              accept at the listed rate, {candidate?.full_name.split(' ')[0]} will be confirmed!
            </>
          ),
        },
      });
    },
    [candidate, dispatch, handlePopupClose, queryClient],
  );

  const handleInviteClick = () => {
    if (selectedJobIds.length > 0) {
      inviteProfessionalsToJobsMutation.mutate(
        {
          professionalIds: [professionalId],
          jobIds: selectedJobIds,
          officeId,
        },
        {
          onSuccess: handleInviteSuccess,
        },
      );
    }
  };

  const onCardPress = (jobId: string) => {
    navigateWithoutBlocking(routes.jobDetails.replace(':jobId', jobId));
  };

  if (!show || !candidate || !job) {
    return null;
  }

  return (
    <Portal>
      <Box
        className="modal"
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 9999,
        }}
      >
        <Box
          className="modal_content"
          style={{
            position: 'relative',
            padding: '40px 30px',
            maxWidth: 700,
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
          }}
          onClick={(event) => event.stopPropagation()}
        >
          <Box style={{ position: 'absolute', top: 20, right: 20, cursor: 'pointer' }}>
            <img alt="close-popup-icon" src={ClosePopupIcon} onClick={onClose} />
          </Box>

          <Box style={{ marginTop: 12, marginBottom: 20, textAlign: 'center' }}>
            {candidate?.profile_image ? (
              <img
                src={candidate.profile_image}
                style={{
                  width: 68,
                  height: 68,
                  borderRadius: 68,
                  objectFit: 'cover',
                }}
                alt="profile_image"
              />
            ) : (
              <Center
                backgroundColor={colors.secondary[500]}
                borderRadius={68}
                width={68}
                height={68}
              >
                <img src={defaultProfile} alt="profile_image" width="40px" height="40px" />
              </Center>
            )}
          </Box>

          <Typography as="span" size="body_large" bold>
            Candidate Confirmed for {moment(job?.local_date).format('ddd, MMM D, YYYY')}
          </Typography>

          <Typography as="span" style={{ marginTop: 16 }}>
            Would you like to invite <Typography as="span" bold>{candidate?.full_name}</Typography> for other shifts?
          </Typography>

          <Box
            ref={scrollContainerRef}
            flex={1}
            marginBottom={24}
            marginTop={16}
            overflowY="auto"
            overflowX="hidden"
            maxHeight="400px"
            scrollbarWidth="thin"
          >
            {isLoading ? (
              <Center
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: 40,
                }}
              >
                <Spinner centered variant="default" size="md" />
              </Center>
            ) : (
              <Box marginHorizontal={8} marginTop={4} gap={10} paddingBottom={8}>
                {eligibleJobs.map((job) => (
                  <EligibleJobCard
                    key={job.id}
                    job={job}
                    isSelected={selectedJobIds.includes(job.id)}
                    onSelect={handleJobSelect}
                    disabled={isInviting}
                    onCardPress={onCardPress}
                  />
                ))}

                {isFetchingNextPage && (
                  <Center
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      padding: 16,
                    }}
                  >
                    <Spinner centered variant="default" size="md" />
                  </Center>
                )}
              </Box>
            )}
          </Box>

          <Row gap={16} justifyContent="center" marginTop={16}>
            <Button
              style={{
                alignSelf: 'center',
                width: 186,
                margin: 0,
                backgroundColor: colors.secondary[500],
              }}
              onClick={onClose}
              text="No Thanks"
              disabled={isInviting}
            />

            <Button
              text={
                selectedJobIds.length > 0
                  ? `Invite for ${selectedJobIds.length} Shift${selectedJobIds.length !== 1 ? 's' : ''}`
                  : 'Invite'
              }
              onClick={handleInviteClick}
              disabled={selectedJobIds.length === 0}
              style={{
                alignSelf: 'center',
                width: 186,
                margin: 0,
                backgroundColor:
                  selectedJobIds.length > 0 ? colors.primary[500] : colors.primary[200],
              }}
              isLoading={isInviting}
            />
          </Row>
        </Box>
      </Box>
    </Portal>
  );
};
